import React from 'react'
import { Modal, Button } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

/**
 * feat-004 story 2.4 (AC5, UX-DR5/J4) — catalog blast-radius disclosure.
 *
 * IMPORTANT (see story Dev Notes "AffectedInstancesConfirm timing"): story
 * 2.1's `updateOption` is a SINGLE atomic call that applies the edit AND
 * computes `liveInstanceCount` together — there is no separate "preview"
 * endpoint. So this is a POST-save disclosure ("this update just changed N
 * live placements"), not a pre-save yes/no gate — the write has already
 * happened by the time this shows. One "Got it" button, not Confirm/Cancel.
 *
 * AntD `Modal` props follow the masterdata clusters modal convention
 * (`components/masterdata/clusters/components/deleteClusterModal.jsx`):
 * `open`/`onCancel`/`centered`/`footer={null}` custom footer, `styles.header/
 * body/content` for dark-mode, RTL via `style.direction`.
 */
export interface AffectedInstancesConfirmProps {
  visible: boolean
  optionName: string
  liveInstanceCount: number
  onClose: () => void
  isRTL?: boolean
  dmode?: boolean
}

export const AffectedInstancesConfirm: React.FC<
  AffectedInstancesConfirmProps
> = ({ visible, optionName, liveInstanceCount, onClose, isRTL, dmode }) => {
  const { t } = useTranslation()

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      centered
      width={420}
      footer={null}
      // Same z-index fix as AddDisplayModeDialog.tsx (2026-10-12): this
      // dialog is also hosted inside `.simv3-confirm` (z-index 1200) when
      // opened from SimulationCanvas's "Manage display options" panel, and
      // AntD's default Modal z-index (~1000) would render it behind that
      // panel, invisible.
      zIndex={1300}
      style={{ direction: isRTL ? 'rtl' : 'ltr' }}
      styles={{
        header: {
          backgroundColor: dmode ? '#2f353e' : '#fff',
          borderBottom: 'none',
        },
        body: { backgroundColor: dmode ? '#2f353e' : '#fff' },
        content: { backgroundColor: dmode ? '#2f353e' : '#fff' },
      }}
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontFamily: 'Mulish, sans-serif',
            fontWeight: 700,
          }}
        >
          <ExclamationCircleOutlined
            style={{ color: '#F39C12', fontSize: 18 }}
          />
          {t('simulationV3.displayOptions.affectedInstances.title')}
        </div>
      }
      data-testid="affected-instances-confirm"
    >
      <p data-testid="affected-instances-copy">
        {t('simulationV3.displayOptions.affectedInstances.body', {
          count: liveInstanceCount,
          name: optionName,
          // fix-030: a user-typed option name carrying `'` or `"` was
          // HTML-escaped by i18next's interpolator and the entity showed
          // up literally in this `<p>` (see BlockedFitDialog's title for
          // the full note). The name sits mid-sentence next to a plural
          // `count`, so it cannot be lifted out of t() — opt this call
          // out of escaping instead; a text node needs none.
          interpolation: { escapeValue: false },
          defaultValue:
            'This changes the {{name}} in the catalog. {{count}} live placement(s) will update immediately — this cannot be undone.',
        })}
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          onClick={onClose}
          data-testid="affected-instances-ok"
        >
          {t('simulationV3.displayOptions.affectedInstances.ok', {
            defaultValue: 'Got it',
          })}
        </Button>
      </div>
    </Modal>
  )
}
